import React, { ReactElement, useState } from 'react';
import { Select, Form, Skeleton, Button, Input, Divider } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { NamePath } from 'antd/lib/form/interface';
import { I18n } from '@aws-amplify/core';

import { useApplicationContext } from '@/context/v1/Application/context';
import { useCreateRiskDamage } from '@/hooks/v1/useRiskDamageMutation';
import { CreateRiskDamageRequest } from '../types/request/riskDamage';
import { RiskDamageDTO } from '@/components/ui/Inputs/types/response';

const { useFormInstance } = Form;

type RiskDamageSelectProps = {
	size?: SizeType;
	loading: boolean;
	disabled: boolean;
	stepKeyId?: string;
	fieldName: NamePath;
	riskDamageId?: string;
	allowCreation?: boolean;
	riskDescriptionId?: string;
	handleClearRiskDamage?: () => void;
	riskDamages: RiskDamageDTO[] | undefined;
	handleSelectRiskDamage?: (id: string) => void;
};

type OptionType = { label: string; value: string } | undefined;

export function RiskDamageSelect({
	loading,
	disabled,
	fieldName,
	stepKeyId,
	riskDamages,
	riskDamageId,
	size = 'middle',
	riskDescriptionId,
	allowCreation = false,
	handleClearRiskDamage,
	handleSelectRiskDamage
}: Readonly<RiskDamageSelectProps>) {
	const [riskDamageName, setRiskDamageName] = useState<string>('');

	const { company, organization } = useApplicationContext();
	const { validateFields } = useFormInstance();

	const { mutateAsync: createRiskDamage, isLoading } = useCreateRiskDamage();

	async function onCreateRiskDamage() {
		const body: CreateRiskDamageRequest = {
			company_id: company.id,
			description: riskDamageName,
			organization_id: organization.id,
			custom_report_step_key_id: stepKeyId,
			risk_description_id: riskDescriptionId
		};

		const { data: riskDamage } = await createRiskDamage({ body });
		const riskDamageId = riskDamage?.id;
		if (riskDamageId) {
			handleChange(riskDamageId);
		}
		setRiskDamageName('');
	}

	async function handleChange(id: string) {
		if (handleSelectRiskDamage) {
			handleSelectRiskDamage(id);
			await validateFields([fieldName]);
		}
	}

	function handleClear() {
		if (handleClearRiskDamage) {
			handleClearRiskDamage();
		}
	}

	function handleRiskDamageNameChange(name: string) {
		setRiskDamageName(name);
	}

	function filterOption(option: OptionType, input: string): boolean {
		return (option?.label.toLowerCase() ?? '').includes(input.toLowerCase());
	}

	function showDropdownrender(menu: ReactElement): JSX.Element {
		if (!allowCreation) {
			return <div>{menu}</div>;
		}
		return (
			<div>
				{menu}
				<Divider style={{ margin: '4px 0' }} />
				<div
					style={{
						display: 'flex',
						flexWrap: 'nowrap',
						padding: 8
					}}
				>
					<Input
						style={{ flex: 'auto', borderRadius: '7px' }}
						value={riskDamageName}
						onChange={(e) => handleRiskDamageNameChange(e.target.value)}
					/>
					<Button
						onClick={onCreateRiskDamage}
						type="link"
						loading={isLoading}
						disabled={!riskDamageName || riskDamageName.length < 3}
					>
						{I18n.get('Add item')}
					</Button>
				</div>
			</div>
		);
	}

	if (loading) {
		return <Skeleton.Input block active size="small" />;
	}

	return (
		<Select
			showSearch
			allowClear
			size={size}
			loading={loading}
			disabled={disabled}
			onClear={handleClear}
			onChange={handleChange}
			value={riskDamageId}
			placeholder={I18n.get('Risk damage')}
			dropdownRender={(menu) => showDropdownrender(menu)}
			filterOption={(input, option) => filterOption(option, input)}
			options={riskDamages?.map(({ id, description }) => ({ label: I18n.get(description), value: id }))}
		/>
	);
}
